import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { UserSwitch, SignOut } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

// Global, always-visible banner shown whenever an admin is impersonating a
// member. Provides the single reliable "exit impersonation → back to Admin"
// control. Rendered inside AppLayout so it appears on every chrome'd page.
export default function ImpersonationBanner() {
  const { impersonation, exitImpersonation } = useAuth();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!impersonation) return null;

  const handleExit = async () => {
    setBusy(true);
    try {
      const ok = await exitImpersonation();
      if (ok) {
        toast.success('Back to your admin account.');
        navigate('/admin/members', { replace: true });
      } else {
        // No valid return ticket — fall back to a clean re-login.
        toast.error('Could not restore your admin session — please sign in again.');
        navigate('/login', { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not exit automatically — please sign in again.');
      navigate('/login', { replace: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 flex items-center justify-between gap-3 bg-[#c9a93a] text-[#013523] px-4 py-2 text-sm font-medium shadow-sm"
    >
      <span className="flex items-center gap-2 min-w-0">
        <UserSwitch size={18} weight="bold" className="shrink-0" />
        <span className="truncate">
          Viewing as <span className="font-semibold" data-testid="impersonation-name">{impersonation.name}</span>
        </span>
      </span>
      <button
        type="button"
        data-testid="impersonation-exit"
        onClick={handleExit}
        disabled={busy}
        className="shrink-0 inline-flex items-center gap-1.5 bg-[#013523] text-white rounded-full px-3 py-1.5 text-xs font-semibold hover:bg-[#024a30] disabled:opacity-60 transition-colors"
      >
        <SignOut size={14} weight="bold" />
        {busy ? 'Returning…' : 'Exit impersonation'}
      </button>
    </div>
  );
}
